import React, { useState, useEffect } from "react";
import EditCustomerModal from "./EditCustomerModal";
import AddCustomerModal from "./AddCustomerModal";
import useAdminData from "../hooks/useAdminData";
import Pagination from "./Pagination";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import { useTheme } from "../context/ThemeContext";
import DeleteCustomerModal from "./DeleteCustomerModal";
import SearchIcon from "@mui/icons-material/Search";
import API_BASE_URL from "../config";
import Tooltip from "@mui/material/Tooltip";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ConfirmCustomerStatusModal from "./ConfirmCustomerStatusModal";

const CustomerList = () => {
  const { customers, fetchCustomers } = useAdminData();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { darkMode, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [errorMessage, setErrorMessage] = useState("");
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [customerToChangeStatus, setCustomerToChangeStatus] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error("Eroare la încărcarea utilizatorilor.");
        }

        const data = await response.json();
        setAllUsers(data);
      } catch (err) {
        console.error("Hiba a felhasználók lekérésekor:", err);
      }
    };

    fetchUsers();
  }, []);

  const handleStatusChange = (customer) => {
    setCustomerToChangeStatus(customer);
    setIsStatusModalOpen(true);
  };

  const handleStatusUpdate = async (
    customerId,
    newStatus,
    refusalReason = ""
  ) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers/${customerId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: newStatus,
            refusalReason, // <<<< EZ HIÁNYZOTT EDDIG
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Eroare la actualizarea statusului.");
      }

      fetchCustomers();
    } catch (error) {
      console.error("Eroare:", error.message);
      setErrorMessage(error.message);
    }
  };

  const filteredCustomers = customers
    .filter((customer) => {
      const searchNormalized = searchQuery
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");

      return (
        (selectedAgent
          ? customer.agent?.id === parseInt(selectedAgent)
          : true) &&
        [
          customer.name,
          customer.phone,
          customer.city,
          customer.county,
          customer.businessType,
          customer.notes,
          customer.type === 0
            ? "clientfinal"
            : customer.type === 1
            ? "clientmarmist"
            : "necunoscut",
        ].some((field) =>
          field?.toString().toLowerCase().includes(searchNormalized)
        )
      );
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;

      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      // Speciális kezelés: Tip Client mező (type)
      if (sortConfig.key === "type") {
        valA = valA === 0 ? "Client Final" : "Client Marmist";
        valB = valB === 0 ? "Client Final" : "Client Marmist";
      }

      // Speciális kezelés: agent.name (ha majd az alapján is akarsz rendezni)
      if (sortConfig.key === "agent") {
        valA = a.agent?.name || "";
        valB = b.agent?.name || "";
      }

      if (valA == null) return 1;
      if (valB == null) return -1;

      const aValue = valA.toString().toLowerCase();
      const bValue = valB.toString().toLowerCase();

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  const displayedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 0: // Prospect
        return "#2196f3"; // KÉK
      case 1: // Ofertare
        return "#ffb300"; // SÁRGA (narancssárga árnyalat, jól olvasható sötét módban is)
      case 2: // Refuzat
        return "#f44336"; // PIROS
      case 3: // Activ
        return "#66bb6a"; // ZÖLD
      default:
        return "#2196f3"; // Default → KÉK
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 0:
        return "Prospect";
      case 1:
        return "Ofertare";
      case 2:
        return "Refuzat";
      case 3:
        return "Activ";
      default:
        return "Necunoscut";
    }
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/customers/admin/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 500) {
          throw new Error(
            "Clientul nu poate fi șters deoarece are comenzi asociate."
          );
        }
        throw new Error("Eroare la ștergerea clientului.");
      }

      setIsDeleteModalOpen(false); // 🔥 Modál bezárása
      setErrorMessage(""); // 🔥 Hibaüzenet törlése, ha sikerült
      fetchCustomers();
    } catch (error) {
      console.error("Eroare:", error.message);
      setErrorMessage(error.message); // 🔥 Hibát mutatjuk a usernek
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  return (
    <>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMessage("")}
          severity="error"
          sx={{ width: "100%", fontSize: "18px", fontWeight: "bold" }}
          variant="filled"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <div
        className={`min-h-screen p-6 ${
          darkMode ? "bg-gray-900" : "bg-gray-200"
        }`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2
            className={`text-2xl font-semibold ${
              darkMode ? "text-white" : "text-black"
            }`}
          >
            Lista clienților
          </h2>

          <div className="flex gap-4">
            {/* 🔥 Szűrő: Ügynökök / Adminok listája */}
            <select
              className={`${
                darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-gray-200 text-black border-gray-400"
              } p-2 border rounded-3xl`}
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
            >
              <option value="">Toți agenții</option>
              {allUsers.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.email})
                </option>
              ))}
            </select>

            <div className="relative flex items-center gap-4">
              <SearchIcon
                className={`absolute left-3 ${
                  darkMode ? "text-gray-300" : "text-gray-500"
                }`}
              />
              <input
                type="text"
                placeholder="Căutare..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-10 pr-4 py-2 border rounded-3xl shadow-sm w-96 transition-all ${
                  darkMode
                    ? "bg-gray-800 text-white border-gray-600 placeholder-gray-400"
                    : "bg-white text-gray-900 border-gray-300 placeholder-gray-500"
                }`}
              />

              {/* 🔥 Új ügyfél hozzáadása gomb */}
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="px-4 py-2 rounded-3xl transition border-2"
                style={{
                  borderColor: darkMode ? "#90caf9" : "#0056B3",
                  color: darkMode ? "#90caf9" : "#ffffff",
                  backgroundColor: darkMode ? "transparent" : "#0056B3",
                  fontWeight: "bold",
                  transition: "all 0.3s ease-in-out",
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = darkMode ? "#90caf9" : "#003B80";
                  e.target.style.backgroundColor = darkMode
                    ? "rgba(144, 202, 249, 0.1)"
                    : "rgba(0, 86, 179, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = darkMode ? "#90caf9" : "#0056B3";
                  e.target.style.backgroundColor = darkMode
                    ? "transparent"
                    : "#0056B3";
                }}
              >
                + Adaugă client
              </button>
            </div>
          </div>
        </div>

        <AddCustomerModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onCustomerAdded={fetchCustomers}
        />

        <div className="overflow-x-auto">
          <table
            className={`w-full table-fixed border ${
              darkMode
                ? "border-gray-700 bg-gray-900 text-gray-300"
                : "border-gray-400 bg-white text-gray-800 shadow-lg"
            }`}
          >
            <thead>
              <tr
                className={`${
                  darkMode
                    ? "bg-gray-700 text-white"
                    : "bg-gray-300 text-gray-900"
                }`}
              >
                {/* <th className="border p-2">ID</th> */}
                <th
                  className="border p-2 w-[10%] cursor-pointer"
                  onClick={() => handleSort("agent")}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>Agent</span>
                    <div className="flex flex-col items-center leading-none gap-[2px]">
                      <div className="flex items-center gap-1">
                        <ArrowUpwardIcon
                          fontSize="small"
                          className={
                            sortConfig.key === "agent" &&
                            sortConfig.direction === "asc"
                              ? ""
                              : "opacity-30"
                          }
                        />
                        <span
                          className={`text-xs ${
                            sortConfig.key === "agent" &&
                            sortConfig.direction === "asc"
                              ? ""
                              : "opacity-30"
                          }`}
                        >
                          A
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ArrowDownwardIcon
                          fontSize="small"
                          className={
                            sortConfig.key === "agent" &&
                            sortConfig.direction === "desc"
                              ? ""
                              : "opacity-30"
                          }
                        />
                        <span
                          className={`text-xs ${
                            sortConfig.key === "agent" &&
                            sortConfig.direction === "desc"
                              ? ""
                              : "opacity-30"
                          }`}
                        >
                          Z
                        </span>
                      </div>
                    </div>
                  </div>
                </th>
                <th
                  className="border p-2 w-[10%] cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>Client</span>
                    <div className="flex flex-col items-center leading-none gap-[2px]">
                      <div className="flex items-center gap-1">
                        <ArrowUpwardIcon
                          fontSize="small"
                          className={
                            sortConfig.key === "name" &&
                            sortConfig.direction === "asc"
                              ? ""
                              : "opacity-30"
                          }
                        />
                        <span
                          className={`text-xs ${
                            sortConfig.key === "name" &&
                            sortConfig.direction === "asc"
                              ? ""
                              : "opacity-30"
                          }`}
                        >
                          A
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ArrowDownwardIcon
                          fontSize="small"
                          className={
                            sortConfig.key === "name" &&
                            sortConfig.direction === "desc"
                              ? ""
                              : "opacity-30"
                          }
                        />
                        <span
                          className={`text-xs ${
                            sortConfig.key === "name" &&
                            sortConfig.direction === "desc"
                              ? ""
                              : "opacity-30"
                          }`}
                        >
                          Z
                        </span>
                      </div>
                    </div>
                  </div>
                </th>

                <th className="border p-2 w-[10%]">Telefon</th>
                <th
                  className="border p-2 w-[10%] cursor-pointer"
                  onClick={() => handleSort("city")}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>Oraș</span>
                    <div className="flex flex-col items-center leading-none gap-[2px]">
                      <div className="flex items-center gap-1">
                        <ArrowUpwardIcon
                          fontSize="small"
                          className={
                            sortConfig.key === "city" &&
                            sortConfig.direction === "asc"
                              ? ""
                              : "opacity-30"
                          }
                        />
                        <span
                          className={`text-xs ${
                            sortConfig.key === "city" &&
                            sortConfig.direction === "asc"
                              ? ""
                              : "opacity-30"
                          }`}
                        >
                          A
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ArrowDownwardIcon
                          fontSize="small"
                          className={
                            sortConfig.key === "city" &&
                            sortConfig.direction === "desc"
                              ? ""
                              : "opacity-30"
                          }
                        />
                        <span
                          className={`text-xs ${
                            sortConfig.key === "city" &&
                            sortConfig.direction === "desc"
                              ? ""
                              : "opacity-30"
                          }`}
                        >
                          Z
                        </span>
                      </div>
                    </div>
                  </div>
                </th>
                <th
                  className="border p-2 w-[10%] cursor-pointer"
                  onClick={() => handleSort("county")}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>Județ</span>
                    <div className="flex flex-col items-center leading-none gap-[2px]">
                      <div className="flex items-center gap-1">
                        <ArrowUpwardIcon
                          fontSize="small"
                          className={
                            sortConfig.key === "county" &&
                            sortConfig.direction === "asc"
                              ? ""
                              : "opacity-30"
                          }
                        />
                        <span
                          className={`text-xs ${
                            sortConfig.key === "county" &&
                            sortConfig.direction === "asc"
                              ? ""
                              : "opacity-30"
                          }`}
                        >
                          A
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ArrowDownwardIcon
                          fontSize="small"
                          className={
                            sortConfig.key === "county" &&
                            sortConfig.direction === "desc"
                              ? ""
                              : "opacity-30"
                          }
                        />
                        <span
                          className={`text-xs ${
                            sortConfig.key === "county" &&
                            sortConfig.direction === "desc"
                              ? ""
                              : "opacity-30"
                          }`}
                        >
                          Z
                        </span>
                      </div>
                    </div>
                  </div>
                </th>
                <th className="border p-2 w-[10%]">Tip Afacere</th>
                <th className="border p-2 w-[15%]">Notițe</th>
                <th
                  className="border p-2 w-[10%] cursor-pointer"
                  onClick={() => handleSort("type")}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>Tip Client</span>
                    <div className="flex flex-col items-center leading-none gap-[2px]">
                      <div className="flex items-center gap-1">
                        <ArrowUpwardIcon
                          fontSize="small"
                          className={
                            sortConfig.key === "type" &&
                            sortConfig.direction === "asc"
                              ? ""
                              : "opacity-30"
                          }
                        />
                        <span
                          className={`text-xs ${
                            sortConfig.key === "type" &&
                            sortConfig.direction === "asc"
                              ? ""
                              : "opacity-30"
                          }`}
                        >
                          A
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ArrowDownwardIcon
                          fontSize="small"
                          className={
                            sortConfig.key === "type" &&
                            sortConfig.direction === "desc"
                              ? ""
                              : "opacity-30"
                          }
                        />
                        <span
                          className={`text-xs ${
                            sortConfig.key === "type" &&
                            sortConfig.direction === "desc"
                              ? ""
                              : "opacity-30"
                          }`}
                        >
                          Z
                        </span>
                      </div>
                    </div>
                  </div>
                </th>
                <th className="border p-2 w-[10%]">Status</th>

                {/* <th className="border p-2">Creat</th> */}
                {/* <th className="border p-2">Actualizat</th> */}
                <th className="border p-2 w-[20%]">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {displayedCustomers.length > 0 ? (
                displayedCustomers.map((customer, index) => (
                  <tr
                    key={customer.id}
                    className={`border ${
                      darkMode
                        ? index % 2 === 0
                          ? "bg-gray-800 hover:bg-gray-700"
                          : "bg-gray-900 hover:bg-gray-700"
                        : index % 2 === 0
                        ? "bg-gray-100 hover:bg-gray-200"
                        : "bg-white hover:bg-gray-200"
                    } transition text-center`}
                  >
                    {/* <td className="border p-2">{customer.id}</td> */}
                    <td className="border p-2 w-[10%] h-[48px] overflow-hidden">
                      <Tooltip
                        title={customer.agent?.name || "Fără agent"}
                        arrow
                      >
                        <div className="line-clamp-2 leading-tight">
                          {customer.agent?.name || "Fără agent"}
                        </div>
                      </Tooltip>
                    </td>

                    <td
                      className="border p-2 max-w-[200px] truncate"
                      title={customer.name}
                    >
                      {customer.name}
                    </td>
                    <td className="border p-2">{customer.phone}</td>
                    <td
                      className="border p-2 max-w-[200px] truncate"
                      title={customer.city}
                    >
                      {customer.city}
                    </td>
                    <td className="border p-2">{customer.county}</td>
                    <td
                      className="border p-2 max-w-[200px] truncate"
                      title={customer.businessType}
                    >
                      {customer.businessType}
                    </td>
                    <td className="border p-2 max-w-[200px]">
                      <Tooltip title={customer.notes} arrow>
                        <span className="block truncate whitespace-nowrap overflow-hidden">
                          {customer.notes}
                        </span>
                      </Tooltip>
                    </td>

                    <td className="border p-2">
                      {customer.type === 0 ? (
                        <span>Final</span>
                      ) : customer.type === 1 ? (
                        <span>Marmist</span>
                      ) : (
                        <span>Necunoscut</span>
                      )}
                    </td>
                    <td className="border p-2">
                      <Button
                        variant="outlined"
                        sx={{
                          minWidth: "110px",
                          textAlign: "center",
                          justifyContent: "center",
                          borderColor: getStatusColor(customer.status),
                          color: getStatusColor(customer.status),
                          fontWeight: "bold",
                          "&:hover": {
                            backgroundColor: `${getStatusColor(
                              customer.status
                            )}1A`, // 10% opacity
                          },
                        }}
                        onClick={() => handleStatusChange(customer)}
                      >
                        {getStatusLabel(customer.status)}
                      </Button>
                    </td>

                    {/* <td className="border p-2">
                    {new Date(customer.createdAt).toLocaleDateString()}
                  </td>
                  <td className="border p-2">
                    {new Date(customer.updatedAt).toLocaleDateString()}
                  </td> */}
                    <td className="border p-2 flex justify-center gap-2">
                      {/* ✏️ Editare gomb */}
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        sx={(theme) => ({
                          borderColor: darkMode ? "#90caf9" : "#0056B3",
                          color: darkMode ? "#90caf9" : "#0056B3",
                          "&:hover": {
                            borderColor: darkMode ? "#90caf9" : "#003B80",
                            backgroundColor: darkMode
                              ? "rgba(144, 202, 249, 0.1)"
                              : "rgba(0, 86, 179, 0.15)",
                          },
                        })}
                        onClick={() => handleEdit(customer)}
                      >
                        Editare
                      </Button>

                      {/* 🗑️ Ștergere gomb */}
                      <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        color="error"
                        onClick={() => {
                          setCustomerToDelete({
                            id: customer.id,
                            name: customer.name,
                          });
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        Ștergere
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center p-4 text-gray-400">
                    Nu există clienți.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />

        <ConfirmCustomerStatusModal
          open={isStatusModalOpen}
          onClose={() => setIsStatusModalOpen(false)}
          currentStatus={customerToChangeStatus?.status}
          initialRefusalReason={customerToChangeStatus?.refusalReason}
          onConfirm={(newStatus, reason) =>
            handleStatusUpdate(customerToChangeStatus.id, newStatus, reason)
          }
        />

        <EditCustomerModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          customer={selectedCustomer}
          onCustomerUpdated={fetchCustomers}
        />

        <DeleteCustomerModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={(id) => handleDelete(id)}
          customer={customerToDelete}
        />
      </div>
    </>
  );
};

export default CustomerList;
